import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { HOMEPAGE } from "../../home/routes";
import { OPERACOES_PAGE } from "../../operacoes/routes";
import { getStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import {
  downloadRegistrationsReportCsvApi,
  downloadRegistrationsReportPdfApi,
  triggerBlobDownload,
  ExportRegistrationsCsvParams,
} from "../reportsApi";

import "./ReportsPage.css";

const STORES_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 350;

type DateMode = "day" | "dayRange" | "slotRange";
type HasPaymentFilter = "" | "true" | "false";
type ReportFormat = "csv" | "pdf";
/** `"all"` = relatório consolidado de todas as lojas. */
type StoreChoice = StorePublic | "all" | null;

const DATE_MODES: { value: DateMode; label: string }[] = [
  { value: "day", label: "Um dia" },
  { value: "dayRange", label: "Intervalo de dias" },
  { value: "slotRange", label: "Por horário do plantão" },
];

const STATUSES: { value: string; label: string }[] = [
  { value: "", label: "Qualquer" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "APROVADO", label: "Aprovado" },
  { value: "REJEITADO", label: "Rejeitado" },
  { value: "FALTOU", label: "Faltou" },
  { value: "AUSENTE", label: "Ausente" },
  { value: "CANCELADO", label: "Cancelado" },
];

const PAYMENT_LABEL: Record<HasPaymentFilter, string> = {
  "": "Qualquer",
  true: "Só com pagamento",
  false: "Só sem pagamento",
};

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function toIsoFromDatetimeLocal(local: string): string {
  const dt = new Date(local);
  if (Number.isNaN(dt.getTime())) throw new Error("Data/hora inválida.");
  return dt.toISOString();
}

/** Atalhos de período — todos viram "intervalo de dias". */
const quickRanges = (today: Date): { label: string; from: string; to: string }[] => {
  const y = today.getFullYear();
  const m = today.getMonth();
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [
    { label: "Hoje", from: toYmd(today), to: toYmd(today) },
    { label: "Esta semana", from: toYmd(monday), to: toYmd(sunday) },
    { label: "Mês passado", from: toYmd(new Date(y, m - 1, 1)), to: toYmd(new Date(y, m, 0)) },
    { label: "Quinzena 1 a 15", from: toYmd(new Date(y, m, 1)), to: toYmd(new Date(y, m, 15)) },
    { label: "Quinzena 16 ao fim", from: toYmd(new Date(y, m, 16)), to: toYmd(new Date(y, m + 1, 0)) },
  ];
};

/**
 * Relatório de agendamentos (inscrições e pagamentos dos freelancers) em CSV ou
 * PDF. Era o modal "Gerar relatórios" de Operações (`GenerateReportsModal`);
 * os parâmetros enviados e as validações são os mesmos — só virou página,
 * com um resumo do que vai ser gerado.
 */
const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(OPERACOES_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const todayYmd = useMemo(() => toYmd(today), [today]);
  const ranges = useMemo(() => quickRanges(today), [today]);

  // Loja
  const [selectedStore, setSelectedStore] = useState<StoreChoice>(null);
  const [storeSearchInput, setStoreSearchInput] = useState("");
  const [storeSearch, setStoreSearch] = useState("");
  const [storesPage, setStoresPage] = useState(1);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [storesTotal, setStoresTotal] = useState(0);
  const [loadingStores, setLoadingStores] = useState(false);
  const [pickingStore, setPickingStore] = useState(true);

  // Período e filtros
  const [reportFormat, setReportFormat] = useState<ReportFormat>("csv");
  const [dateMode, setDateMode] = useState<DateMode>("day");
  const [day, setDay] = useState(todayYmd);
  const [dayFrom, setDayFrom] = useState(todayYmd);
  const [dayTo, setDayTo] = useState(todayYmd);
  const [slotFromLocal, setSlotFromLocal] = useState("");
  const [slotToLocal, setSlotToLocal] = useState("");
  const [status, setStatus] = useState("");
  const [hasPayment, setHasPayment] = useState<HasPaymentFilter>("");
  const [includeCanceledSlots, setIncludeCanceledSlots] = useState(false);
  const [cpf, setCpf] = useState("");
  const [freelancerName, setFreelancerName] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setStoreSearch(storeSearchInput.trim());
      setStoresPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [storeSearchInput]);

  useEffect(() => {
    if (!token || !pickingStore) return;
    let active = true;
    setLoadingStores(true);
    getStoresPaginatedApi(token, { page: storesPage, limit: STORES_LIMIT, ...(storeSearch ? { nome: storeSearch } : {}) })
      .then((data) => {
        if (!active) return;
        setStores(data.items);
        setStoresTotal(data.total);
      })
      .catch(() => active && toast.error("Erro ao buscar lojas."))
      .finally(() => active && setLoadingStores(false));
    return () => {
      active = false;
    };
  }, [token, storesPage, storeSearch, pickingStore]);

  const chooseStore = (choice: StorePublic | "all") => {
    setSelectedStore(choice);
    setPickingStore(false);
  };

  const applyRange = (from: string, to: string) => {
    setDateMode("dayRange");
    setDayFrom(from);
    setDayTo(to);
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStore) {
      toast.error("Selecione uma loja.");
      return;
    }

    const params: ExportRegistrationsCsvParams = {};
    if (selectedStore !== "all") params.storeId = selectedStore.id;

    try {
      if (dateMode === "day") {
        if (!day) {
          toast.error("Selecione um dia.");
          return;
        }
        params.day = day;
      }
      if (dateMode === "dayRange") {
        if (!dayFrom || !dayTo) {
          toast.error("Preencha 'De' e 'Até'.");
          return;
        }
        if (dayTo < dayFrom) {
          toast.error("'Até' não pode ser menor que 'De'.");
          return;
        }
        params.dayFrom = dayFrom;
        params.dayTo = dayTo;
      }
      if (dateMode === "slotRange") {
        if (!slotFromLocal || !slotToLocal) {
          toast.error("Preencha 'De (data/hora)' e 'Até (data/hora)'.");
          return;
        }
        const fromIso = toIsoFromDatetimeLocal(slotFromLocal);
        const toIso = toIsoFromDatetimeLocal(slotToLocal);
        if (new Date(toIso).getTime() <= new Date(fromIso).getTime()) {
          toast.error("'Até' deve ser maior que 'De'.");
          return;
        }
        params.from = fromIso;
        params.to = toIso;
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Período inválido.");
      return;
    }

    if (status) params.status = status;
    if (hasPayment === "true") params.hasPayment = true;
    if (hasPayment === "false") params.hasPayment = false;
    params.includeCanceledSlots = includeCanceledSlots;
    if (cpf.trim()) params.cpf = cpf.trim();
    if (freelancerName.trim()) params.freelancerName = freelancerName.trim();

    try {
      setGenerating(true);
      const res =
        reportFormat === "pdf"
          ? await downloadRegistrationsReportPdfApi(params)
          : await downloadRegistrationsReportCsvApi(params);
      triggerBlobDownload(res.blob, res.filename);
      toast.success("Relatório gerado!");
    } catch {
      toast.error("Não foi possível gerar o relatório.");
    } finally {
      setGenerating(false);
    }
  };

  const storeLabel = selectedStore === "all" ? "Todas as lojas" : selectedStore?.nomeFantasia ?? "Escolha uma loja";

  const periodLabel = useMemo(() => {
    if (dateMode === "day") return day ? formatBrDate(day) : "—";
    if (dateMode === "dayRange") {
      if (!dayFrom || !dayTo) return "—";
      return dayFrom === dayTo ? formatBrDate(dayFrom) : `${formatBrDate(dayFrom)} a ${formatBrDate(dayTo)}`;
    }
    if (!slotFromLocal || !slotToLocal) return "—";
    const fmt = (v: string) => new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    return `${fmt(slotFromLocal)} a ${fmt(slotToLocal)}, por horário do plantão`;
  }, [dateMode, day, dayFrom, dayTo, slotFromLocal, slotToLocal]);

  const generateLabel = generating ? "Gerando..." : reportFormat === "pdf" ? "Gerar PDF" : "Gerar CSV";
  const storeAddress = (s: StorePublic) => [s.endereco, s.estado].filter(Boolean).join(" · ") || "Endereço não informado";

  return (
    <div className="repPage">
      <main className="repPage__main">
        <EcoPageHeader
          crumbs={[
            { label: "Início", onClick: () => navigate(HOMEPAGE()) },
            { label: "Operações", onClick: () => navigate(OPERACOES_PAGE()) },
            { label: "Relatórios" },
          ]}
          title="Relatório de agendamentos"
          subtitle="Inscrições e pagamentos dos freelancers por loja e período, para conferência e folha."
          onBack={goBack}
          backLabel="Voltar para Operações"
        />

        <form className="repLayout" onSubmit={handleGenerate}>
          <div className="repSteps">
            {/* 1. Loja */}
            <section className="ecoCard repStep" aria-labelledby="rep-loja">
              <div className="repStep__head">
                <span className="repStep__num" aria-hidden="true">1</span>
                <h2 id="rep-loja" className="repStep__title">Loja</h2>
              </div>

              <div className="repStore__options">
                <button
                  type="button"
                  className={`repOpt${selectedStore === "all" ? " is-selected" : ""}`}
                  aria-pressed={selectedStore === "all"}
                  onClick={() => chooseStore("all")}
                >
                  <span className="repOpt__check" aria-hidden="true">
                    {selectedStore === "all" ? <EcoIcon name="check" size={16} strokeWidth={2.4} /> : null}
                  </span>
                  <span className="repOpt__text">
                    <span className="repOpt__title">Todas as lojas</span>
                    <span className="repOpt__desc">Relatório consolidado</span>
                  </span>
                </button>

                {selectedStore && selectedStore !== "all" ? (
                  <div className="repOpt is-selected">
                    <span className="repOpt__check" aria-hidden="true">
                      <EcoIcon name="check" size={16} strokeWidth={2.4} />
                    </span>
                    <span className="repOpt__text">
                      <span className="repOpt__title">{selectedStore.nomeFantasia}</span>
                      <span className="repOpt__desc">{storeAddress(selectedStore)}</span>
                    </span>
                    <button type="button" className="ecoLinkBtn" onClick={() => setPickingStore(true)}>
                      Trocar
                    </button>
                  </div>
                ) : null}
              </div>

              {pickingStore || !selectedStore ? (
                <div className="repStore__picker">
                  <label htmlFor="rep-busca" className="ecoLabel">
                    {selectedStore ? "Buscar outra loja" : "Ou escolha uma loja"}
                  </label>
                  <div className="ecoSearch">
                    <EcoIcon name="search" size={18} className="ecoSearch__icon" />
                    <input
                      id="rep-busca"
                      type="search"
                      className="ecoInput"
                      placeholder="Buscar loja pelo nome"
                      value={storeSearchInput}
                      onChange={(e) => setStoreSearchInput(e.target.value)}
                    />
                  </div>
                  {!loadingStores && stores.length === 0 ? (
                    <p className="ecoEmpty">Nenhuma loja encontrada.</p>
                  ) : (
                    <ul className="repStore__list" aria-busy={loadingStores}>
                      {stores.map((s) => (
                        <li key={s.id}>
                          <button
                            type="button"
                            className={`repStore__item${selectedStore !== "all" && selectedStore?.id === s.id ? " is-selected" : ""}`}
                            onClick={() => chooseStore(s)}
                          >
                            <span className="repStore__name">{s.nomeFantasia}</span>
                            <span className="repStore__addr">{storeAddress(s)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <EcoPagination page={storesPage} pageSize={STORES_LIMIT} total={storesTotal} onChange={setStoresPage} disabled={loadingStores} />
                </div>
              ) : null}
            </section>

            {/* 2. Período */}
            <section className="ecoCard repStep" aria-labelledby="rep-periodo">
              <div className="repStep__head">
                <span className="repStep__num" aria-hidden="true">2</span>
                <h2 id="rep-periodo" className="repStep__title">Período</h2>
              </div>

              <EcoSegmented label="Tipo de período" options={DATE_MODES} value={dateMode} onChange={setDateMode} className="repStep__segmented" />

              {dateMode === "day" ? (
                <div className="ecoField repStep__narrow">
                  <label htmlFor="rep-dia">Dia</label>
                  <input id="rep-dia" type="date" className="ecoInput" value={day} onChange={(e) => setDay(e.target.value)} />
                </div>
              ) : null}

              {dateMode === "dayRange" ? (
                <div className="repStep__grid">
                  <div className="ecoField">
                    <label htmlFor="rep-de">De</label>
                    <input id="rep-de" type="date" className="ecoInput" value={dayFrom} max={dayTo || undefined} onChange={(e) => setDayFrom(e.target.value)} />
                  </div>
                  <div className="ecoField">
                    <label htmlFor="rep-ate">Até</label>
                    <input id="rep-ate" type="date" className="ecoInput" value={dayTo} min={dayFrom || undefined} onChange={(e) => setDayTo(e.target.value)} />
                  </div>
                </div>
              ) : null}

              {dateMode === "slotRange" ? (
                <div className="repStep__grid">
                  <div className="ecoField">
                    <label htmlFor="rep-slot-de">De (data e hora)</label>
                    <input id="rep-slot-de" type="datetime-local" className="ecoInput" value={slotFromLocal} onChange={(e) => setSlotFromLocal(e.target.value)} />
                  </div>
                  <div className="ecoField">
                    <label htmlFor="rep-slot-ate">Até (data e hora)</label>
                    <input id="rep-slot-ate" type="datetime-local" className="ecoInput" value={slotToLocal} onChange={(e) => setSlotToLocal(e.target.value)} />
                  </div>
                </div>
              ) : null}

              {dateMode !== "slotRange" ? (
                <div className="repChips" role="group" aria-label="Atalhos de período">
                  {ranges.map((r) => {
                    const active = dateMode === "dayRange" && dayFrom === r.from && dayTo === r.to;
                    return (
                      <button
                        key={r.label}
                        type="button"
                        className={`repChip${active ? " is-active" : ""}`}
                        aria-pressed={active}
                        onClick={() => applyRange(r.from, r.to)}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </section>

            {/* 3. Filtros */}
            <section className="ecoCard repStep" aria-labelledby="rep-filtros">
              <div className="repStep__head repStep__head--split">
                <div className="repStep__headLeft">
                  <span className="repStep__num" aria-hidden="true">3</span>
                  <h2 id="rep-filtros" className="repStep__title">Filtros</h2>
                </div>
                <span className="repStep__optional">Opcionais</span>
              </div>

              <div className="repStep__grid">
                <div className="ecoField">
                  <label htmlFor="rep-status">Status da inscrição</label>
                  <select id="rep-status" className="ecoSelect" value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ecoField">
                  <label htmlFor="rep-pag">Pagamento</label>
                  <select id="rep-pag" className="ecoSelect" value={hasPayment} onChange={(e) => setHasPayment(e.target.value as HasPaymentFilter)}>
                    <option value="">Qualquer</option>
                    <option value="true">Só com pagamento</option>
                    <option value="false">Só sem pagamento</option>
                  </select>
                </div>
                <div className="ecoField">
                  <label htmlFor="rep-nome">Nome do freelancer</label>
                  <input id="rep-nome" className="ecoInput" placeholder="Parte do nome" value={freelancerName} onChange={(e) => setFreelancerName(e.target.value)} />
                </div>
                <div className="ecoField">
                  <label htmlFor="rep-cpf">CPF do freelancer</label>
                  <input id="rep-cpf" className="ecoInput" inputMode="numeric" placeholder="Só números ou com pontuação" value={cpf} onChange={(e) => setCpf(e.target.value)} />
                </div>
              </div>

              <label className="repCheck">
                <input type="checkbox" checked={includeCanceledSlots} onChange={(e) => setIncludeCanceledSlots(e.target.checked)} />
                <span>Incluir plantões cancelados</span>
              </label>
            </section>
          </div>

          <aside className="repAside">
            {/* Formato */}
            <section className="ecoCard repStep" aria-labelledby="rep-formato">
              <h2 id="rep-formato" className="repStep__title">Formato do arquivo</h2>
              <div className="repFormats" role="radiogroup" aria-labelledby="rep-formato">
                {([
                  ["csv", "CSV para planilha", "Abre no Excel. Uma linha por inscrição, com valores, PIX, banco e ajustes."],
                  ["pdf", "PDF para conferência", "Pronto para conferir e imprimir."],
                ] as [ReportFormat, string, string][]).map(([value, title, desc]) => (
                  <label key={value} className={`repOpt repOpt--radio${reportFormat === value ? " is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="rep-format"
                      value={value}
                      checked={reportFormat === value}
                      onChange={() => setReportFormat(value)}
                      className="ecoVisuallyHidden"
                    />
                    <span className="repOpt__check repOpt__check--round" aria-hidden="true">
                      {reportFormat === value ? <span className="repOpt__dot" /> : null}
                    </span>
                    <span className="repOpt__text">
                      <span className="repOpt__title">{title}</span>
                      <span className="repOpt__desc">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </section>

            {/* Resumo + gerar */}
            <section className="ecoCard repStep repSummary" aria-labelledby="rep-resumo">
              <h2 id="rep-resumo" className="repStep__title">Resumo</h2>
              <dl className="repSummary__list">
                <dt>Loja</dt>
                <dd>{storeLabel}</dd>
                <dt>Período</dt>
                <dd>{periodLabel}</dd>
                <dt>Status</dt>
                <dd>{STATUSES.find((s) => s.value === status)?.label ?? status}</dd>
                <dt>Pagamento</dt>
                <dd>{PAYMENT_LABEL[hasPayment]}</dd>
                {freelancerName.trim() || cpf.trim() ? (
                  <>
                    <dt>Freelancer</dt>
                    <dd>{[freelancerName.trim(), cpf.trim()].filter(Boolean).join(" · ")}</dd>
                  </>
                ) : null}
                <dt>Cancelados</dt>
                <dd>{includeCanceledSlots ? "Incluídos" : "Fora do relatório"}</dd>
              </dl>

              {reportFormat === "csv" ? (
                <div className="repSummary__columns">
                  <span>Colunas do CSV</span>
                  <p>
                    Nome, CPF, PIX, valor final, status, decisão e quem decidiu, dia da inscrição, loja, dia e horário do
                    plantão, afiliação, banco, pagamento, horas extras, saída antecipada e ajustes.
                  </p>
                </div>
              ) : null}

              <div className="repSummary__submit">
                <button type="submit" className="ecoBtn ecoBtn--primary repSubmit" disabled={generating || !selectedStore}>
                  <EcoIcon name="download" size={18} />
                  {generateLabel}
                </button>
                <p className="repSummary__hint">
                  {selectedStore
                    ? `${storeLabel} · ${periodLabel}. O arquivo baixa direto no navegador.`
                    : "Escolha a loja (ou todas) para gerar o relatório."}
                </p>
              </div>
            </section>
          </aside>
        </form>
      </main>
    </div>
  );
};

export default ReportsPage;
